import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from './utils';
import { Chapter } from './StoryHero';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const Stepper = ({ label, value, onChange, testId, min = 1 }) => (
  <div className="flex items-center justify-between border-b border-[#E5DCCF] py-2.5">
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60">{label}</span>
    <div className="flex items-center gap-1">
      <button onClick={() => onChange(Math.max(min, value - 1))} className="w-7 h-7 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors" data-testid={`${testId}-dec`} aria-label={`Decrease ${label}`}>
        <Minus className="w-3 h-3" strokeWidth={1.5} />
      </button>
      <input
        type="number" min={min} value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-14 text-center bg-transparent text-[#0A1710] font-semibold tabular-nums text-sm"
        data-testid={testId}
      />
      <button onClick={() => onChange(value + 1)} className="w-7 h-7 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors" data-testid={`${testId}-inc`} aria-label={`Increase ${label}`}>
        <Plus className="w-3 h-3" strokeWidth={1.5} />
      </button>
    </div>
  </div>
);

export const AvailabilitySection = ({ availability, decorBasePrice, onEnquire }) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [rooms, setRooms] = useState(10);
  const [guests, setGuests] = useState(100);
  const [plateType, setPlateType] = useState('veg');

  const booked = useMemo(() => new Set(availability?.booked_dates || []), [availability]);
  const pricing = availability?.pricing || {};

  const nights = checkIn && checkOut
    ? Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)
    : 0;
  const sessions = nights * 2;
  const stayCost = rooms * (pricing.room_price_per_night || 0) * nights;
  const platePrice = plateType === 'veg' ? (pricing.veg_plate || 0) : (pricing.nonveg_plate || 0);
  const foodCost = guests * platePrice * sessions;
  const total = stayCost + foodCost + (decorBasePrice || 0);

  const rangeHasBooked = (startIso, endIso) => {
    const d = new Date(startIso);
    const end = new Date(endIso);
    while (d < end) {
      d.setDate(d.getDate() + 1);
      const iso = d.toISOString().slice(0, 10);
      if (iso !== endIso && booked.has(iso)) return true;
    }
    return false;
  };

  const handleDayClick = (iso) => {
    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(iso);
      setCheckOut(null);
    } else if (iso > checkIn) {
      if (rangeHasBooked(checkIn, iso)) {
        toast.error('Those dates include a booked day — starting a fresh selection.');
        setCheckIn(iso);
        setCheckOut(null);
        return;
      }
      setCheckOut(iso);
    } else {
      setCheckIn(iso);
      setCheckOut(null);
    }
  };

  const renderMonth = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayISO = toISO(today);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = toISO(new Date(year, month, d));
      const isBooked = booked.has(iso);
      const isPast = iso < todayISO;
      const isSelected = iso === checkIn || iso === checkOut;
      const inRange = checkIn && checkOut && iso > checkIn && iso < checkOut;
      cells.push(
        <button
          key={iso}
          disabled={isBooked || isPast}
          onClick={() => handleDayClick(iso)}
          data-testid={`calendar-day-${iso}`}
          className={`aspect-square flex items-center justify-center text-[13px] tabular-nums transition-colors
            ${isPast ? 'text-[#0A1710]/20 cursor-not-allowed' : ''}
            ${isBooked ? 'bg-[#9E4040]/10 text-[#9E4040]/50 line-through cursor-not-allowed' : ''}
            ${isSelected ? 'bg-[#C6A87C] text-[#0A1710] font-bold' : ''}
            ${inRange ? 'bg-[#C6A87C]/20 text-[#0A1710]' : ''}
            ${!isPast && !isBooked && !isSelected && !inRange ? 'hover:bg-[#F3EFEB] text-[#1A3626]' : ''}`}
        >
          {d}
        </button>
      );
    }
    return cells;
  };

  return (
    <section className="bg-[#1A3626] py-16 md:py-24 font-wvc-body" data-testid="story-availability">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <Chapter n={2} label="Availability & Budget" light />
          <h2 className="font-wvc-display text-4xl sm:text-5xl font-light text-[#FDFCF8] tracking-tight leading-[1.05] mt-6">Begin with your dates</h2>
          <p className="text-[#FDFCF8]/60 mt-5 text-sm font-light leading-relaxed max-w-md">{availability?.intro}</p>
          <p className="text-[#C6A87C] font-semibold mt-5 text-sm max-w-md">
            Select check-in and check-out — your personalised budget estimate opens the moment you do.
          </p>
          <div className="flex items-center gap-6 mt-8 text-[10px] uppercase tracking-[0.15em] text-[#FDFCF8]/50 font-bold">
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-[#FDFCF8]/15 border border-[#FDFCF8]/30 inline-block" /> Available</span>
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-[#9E4040]/50 inline-block" /> Booked</span>
            <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-[#C6A87C] inline-block" /> Selected</span>
          </div>
        </div>

        <div className="lg:col-span-7 bg-[#FDFCF8] border border-[#2A4D38] p-5 md:p-8" data-testid="availability-calendar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className="w-8 h-8 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors" data-testid="calendar-prev">
                  <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <p className="font-wvc-display text-xl text-[#0A1710]">{MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}</p>
                <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className="w-8 h-8 flex items-center justify-center border border-[#E5DCCF] hover:bg-[#1A3626] hover:text-[#FDFCF8] transition-colors" data-testid="calendar-next">
                  <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/50 mb-3">
                Step {!checkIn || (checkIn && checkOut) ? '1' : '2'} · Tap your {!checkIn || (checkIn && checkOut) ? 'check-in' : 'check-out'} date
              </p>
              <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] uppercase tracking-wider text-[#1A3626]/40 font-bold mb-1.5">
                {DAYS.map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-0.5">{renderMonth()}</div>
            </div>

            <div className={checkIn && checkOut ? '' : 'flex items-center justify-center'}>
              {checkIn && checkOut ? (
                <div data-testid="budget-estimate">
                  <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C]">Your Estimate</p>
                  <p className="font-wvc-display text-2xl text-[#0A1710] mt-1">{nights} night{nights > 1 ? 's' : ''} <span className="text-[#1A3626]/50 text-base">· {checkIn} → {checkOut}</span></p>
                  <div className="mt-4">
                    <Stepper label="Rooms" value={rooms} onChange={setRooms} testId="estimate-rooms-input" />
                    <Stepper label="Guests" value={guests} onChange={setGuests} testId="estimate-guests-input" />
                    <div className="flex items-center justify-between border-b border-[#E5DCCF] py-2.5">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60">Menu</span>
                      <div className="flex border border-[#E5DCCF]">
                        {['veg', 'nonveg'].map(t => (
                          <button key={t} onClick={() => setPlateType(t)}
                            className={`px-3 py-1 text-xs font-semibold transition-colors ${plateType === t ? 'bg-[#1A3626] text-[#FDFCF8]' : 'text-[#1A3626]'}`}
                            data-testid={`estimate-plate-${t}`}>
                            {t === 'veg' ? 'Veg' : 'Non-Veg'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 text-[13px] tabular-nums">
                    <div className="flex justify-between"><span className="text-[#1A3626]/60">Stay ({rooms} × {nights}n)</span><span className="text-[#0A1710]">{formatINR(stayCost)}</span></div>
                    <div className="flex justify-between"><span className="text-[#1A3626]/60">Food ({guests} × {sessions} sessions)</span><span className="text-[#0A1710]">{formatINR(foodCost)}</span></div>
                    <div className="flex justify-between"><span className="text-[#1A3626]/60">Base décor</span><span className="text-[#0A1710]">{formatINR(decorBasePrice)}</span></div>
                  </div>
                  <div className="border-t-2 border-[#0A1710] mt-3 pt-3 flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3626]/60">Approx. total</span>
                    <span className="font-wvc-display text-3xl text-[#0A1710] tabular-nums" data-testid="estimate-total">{formatINR(total)}</span>
                  </div>
                  <button
                    onClick={() => onEnquire && onEnquire(`${checkIn} to ${checkOut}`, String(guests))}
                    className="w-full mt-4 bg-[#C6A87C] text-[#0A1710] hover:bg-[#1A3626] hover:text-[#FDFCF8] py-3.5 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors"
                    data-testid="estimate-enquire-btn"
                  >
                    Talk to WVC Team about these dates
                  </button>
                  <p className="text-[10px] text-[#1A3626]/40 mt-2.5 leading-relaxed">Indicative estimate. Final pricing depends on season, menu and customisations.</p>
                </div>
              ) : (
                <p className="text-center text-[#1A3626]/40 text-sm font-light px-6">Select your dates on the calendar to reveal a personalised budget estimate.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
